import { Star } from 'lucide-react'
import { clsx } from 'clsx'
import type { ProviderReview } from '@lib/supabase/provider.service'

function getInitials(name: string) {
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

function formatReviewDate(dateString: string) {
  if (!dateString || Number.isNaN(new Date(dateString).getTime())) return 'Date unavailable'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function StarRow({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/25'}
        />
      ))}
    </div>
  )
}

function ReviewCard({ review }: { review: ProviderReview }) {
  return (
    <div className="border border-border rounded-xl p-4 space-y-2 bg-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-primary">{getInitials(review.patientName || '?')}</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-medium text-foreground truncate">{review.patientName || 'Anonymous'}</p>
              {review.verified && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                  ✓
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{formatReviewDate(review.date)}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <StarRow rating={review.rating} size={12} />
          {review.service && (
            <p className="text-xs text-muted-foreground mt-1">{review.service}</p>
          )}
        </div>
      </div>
      {review.comment && (
        <p className="text-sm text-muted-foreground leading-relaxed">{review.comment}</p>
      )}
    </div>
  )
}

function RatingBreakdown({
  averageRating,
  totalReviews,
  reviews,
}: {
  averageRating: number
  totalReviews: number
  reviews: ProviderReview[]
}) {
  const distribution = reviews.reduce<Record<number, number>>((acc, review) => {
    acc[review.rating] = (acc[review.rating] || 0) + 1
    return acc
  }, {})

  return (
    <div className="bg-muted/40 border border-border rounded-xl p-5 mb-5">
      <div className="text-center mb-5">
        <p className="text-4xl font-bold text-foreground">{averageRating.toFixed(1)}</p>
        <div className="flex justify-center mt-2">
          <StarRow rating={Math.round(averageRating)} size={16} />
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = distribution[rating] || 0
          const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0
          return (
            <div key={rating} className="flex items-center gap-2">
              <span className="text-sm text-foreground w-3">{rating}</span>
              <Star size={12} className="text-yellow-400 fill-yellow-400 shrink-0" />
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-5 text-right">{count}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export interface ProviderReviewsPanelProps {
  reviews: ProviderReview[]
  averageRating: number
  totalReviews: number
  compact?: boolean
  maxReviews?: number
  className?: string
}

export default function ProviderReviewsPanel({
  reviews,
  averageRating,
  totalReviews,
  compact = false,
  maxReviews,
  className,
}: ProviderReviewsPanelProps) {
  const visibleReviews = maxReviews ? reviews.slice(0, maxReviews) : reviews

  if (reviews.length === 0) {
    return (
      <div className={clsx('text-center py-8 bg-muted/30 border border-border rounded-xl', className)}>
        <p className="text-sm font-medium text-foreground">No reviews yet</p>
        <p className="text-xs text-muted-foreground mt-1">Be the first to review this provider</p>
      </div>
    )
  }

  return (
    <div className={className}>
      {!compact && (
        <RatingBreakdown
          averageRating={averageRating}
          totalReviews={totalReviews}
          reviews={reviews}
        />
      )}
      <div className="space-y-3">
        {visibleReviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>
    </div>
  )
}

export { StarRow, ReviewCard }
